/*
 * Precarga del editor de documentos desde el correo (O4, auditoría 2026-10-09).
 *
 * Medido en docs.orvian.mx del 29-sep al 9-oct (130 aperturas reales): con el motor ya en la caché del
 * navegador, un documento queda listo en 2 s (mediana); cuando hay que bajarlo (~11 MB), el 10 % más lento
 * tarda 57 s o más, con un máximo de 103 s. Así que se baja ANTES, oculto y sin prisa, mientras se lee el correo:
 * public/precarga-motor.html (servida por docs.orvian.mx, donde vive el motor) pide los mismos archivos que
 * el editor. Correo y docs son el mismo sitio (orvian.mx), así que la caché del navegador es la misma.
 *
 * No en el teléfono ni con ahorro de datos. Como mucho una vez al día por navegador: si ya está en caché,
 * repetirla no gasta red (archivos con caché de un año), y si el motor se actualizó, baja la versión nueva.
 */
import { DOCS_URL, docsActivo, enDocs } from './dominios';

const CLAVE = 'orvian_motor_precargado'; // { version, tipos, t }
const VIGENCIA_MS = 24 * 3600 * 1000;
const MAXIMO_MS = 5 * 60 * 1000;
export const TIPOS_DEL_MOTOR = ['word', 'cell'];

let enCurso = false;

function conexionLimitada() {
  const c = typeof navigator !== 'undefined' ? navigator.connection : null;
  return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
}

function esTelefono() {
  return !!window.matchMedia?.('(max-width: 767px), (pointer: coarse) and (hover: none)').matches;
}

function leer() {
  try {
    return JSON.parse(localStorage.getItem(CLAVE) || 'null');
  } catch {
    return null;
  }
}

/** Baja en segundo plano el motor de los tipos pedidos ('word', 'cell'). Devuelve true si empezó. */
export function precalentarMotor({ tipos = TIPOS_DEL_MOTOR } = {}) {
  if (enCurso || !docsActivo() || enDocs() || esTelefono() || conexionLimitada()) return false;
  const previo = leer();
  const reciente = previo && Date.now() - (previo.t || 0) < VIGENCIA_MS ? previo.tipos || [] : [];
  const faltan = tipos.filter((t) => TIPOS_DEL_MOTOR.includes(t) && !reciente.includes(t));
  if (!faltan.length) return false;

  enCurso = true;
  const marco = document.createElement('iframe');
  let reloj = null;
  const terminar = () => {
    window.removeEventListener('message', alMensaje);
    clearTimeout(reloj);
    marco.remove();
    enCurso = false;
  };
  function alMensaje(e) {
    if (e.origin !== DOCS_URL || e.source !== marco.contentWindow || e.data?.tipo !== 'orvian-motor:precarga') return;
    if (e.data.ok) {
      const tiposListos = [...new Set([...(previo?.version === e.data.version ? reciente : []), ...faltan])];
      try {
        localStorage.setItem(CLAVE, JSON.stringify({ version: e.data.version, tipos: tiposListos, t: Date.now() }));
      } catch {
        /* sin almacenamiento: se volverá a intentar otro día */
      }
    }
    terminar();
  }
  reloj = setTimeout(terminar, MAXIMO_MS);
  window.addEventListener('message', alMensaje);
  marco.src = `${DOCS_URL}/precarga-motor.html?tipos=${faltan.join(',')}`;
  marco.title = 'Precarga del editor de documentos';
  marco.setAttribute('aria-hidden', 'true');
  marco.tabIndex = -1;
  marco.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(marco);
  return true;
}

/** Igual, pero cuando la página lleva un rato abierta y el navegador está libre. Devuelve con qué cancelar. */
export function precalentarCuandoEsteLibre(opciones, esperaMs = 15000) {
  let idle = null;
  const t = setTimeout(() => {
    const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
    idle = ric(() => precalentarMotor(opciones), { timeout: 10000 });
  }, esperaMs);
  return () => {
    clearTimeout(t);
    if (idle != null) (window.cancelIdleCallback || clearTimeout)(idle);
  };
}

/** Tipos del motor que usarán estos adjuntos de un correo (para precargar solo eso). */
export function tiposDeAdjuntos(nombres = []) {
  const tipos = new Set();
  nombres.forEach((n) => {
    const nombre = (n || '').toLowerCase();
    if (/\.(xlsx|xlsm|xls|ods|csv)$/.test(nombre)) tipos.add('cell');
    else if (/\.(docx|docm|doc|odt|rtf)$/.test(nombre)) tipos.add('word');
  });
  return [...tipos];
}
